"use client"

import { Card, CardContent } from "@/components/ui/card"
import { BreadcrumbComponent } from "@/components/ui/breadcrumb"
import { Eye, Lock, UserCheck } from "lucide-react"
import { HeroSection } from "@/components/ui/hero-section"

export function PrivacyContent() {
  return (
    <div className="min-h-screen relative overflow-hidden">
      <div className="relative px-4 pt-6 md:pt-8">
        <div className="max-w-4xl mx-auto">
          <div className="mb-2">
            <BreadcrumbComponent items={[{ label: "Home", href: "/" }, { label: "Privacy Policy" }]} />
          </div>

          <HeroSection
            size="minimal"
            showSparkle={false}
            showGlow={false}
            title="Privacy Policy"
            subtitle="Last updated: August 9, 2026"
          />

          <Card className="bg-white/95 backdrop-blur-xs border-green-200 shadow-2xl">
            <CardContent className="p-4 sm:p-6 md:p-8 lg:p-12">
              <div className="prose prose-lg max-w-none">
                <div className="grid md:grid-cols-3 gap-4 sm:gap-6 md:gap-8 mb-8 md:mb-12">
                  <div className="text-center p-6 bg-green-50 rounded-xl">
                    <Eye className="w-8 h-8 text-green-600 mx-auto mb-3" />
                    <h3 className="font-bold text-gray-900 mb-2">Transparency</h3>
                    <p className="text-gray-600">We're clear about what data we collect and why</p>
                  </div>
                  <div className="text-center p-6 bg-green-50 rounded-xl">
                    <Lock className="w-8 h-8 text-green-600 mx-auto mb-3" />
                    <h3 className="font-bold text-gray-900 mb-2">Security</h3>
                    <p className="text-gray-600">Your data is encrypted and securely stored</p>
                  </div>
                  <div className="text-center p-6 bg-green-50 rounded-xl">
                    <UserCheck className="w-8 h-8 text-green-600 mx-auto mb-3" />
                    <h3 className="font-bold text-gray-900 mb-2">Control</h3>
                    <p className="text-gray-600">You control your data and can delete it anytime</p>
                  </div>
                </div>

                <h2 className="text-2xl font-bold text-gray-900 mb-4">Information We Collect</h2>
                <p className="text-gray-700 mb-6">
                  When you use What Grass Is This?, we collect information to provide you with personalized lawn care
                  recommendations:
                </p>
                <ul className="list-disc pl-6 mb-8 space-y-2 text-gray-700">
                  <li>
                    <strong>Account information:</strong> Your name, email address, and user ID when you create or sign in to an account
                  </li>
                  <li>
                    <strong>Device identifiers:</strong> A random app-generated install ID used to protect anonymous grass scans from abuse
                  </li>
                  <li>
                    <strong>Photos:</strong> Images of your lawn that you upload for grass type identification
                  </li>
                  <li>
                    <strong>Community submissions and activity:</strong> Lawn photos, titles, grass types, project types, approximate photo months, timeframes, starting conditions, work performed, outcomes, optional social handles and email addresses, the first three digits of a submitted ZIP code, and a browser identifier used to limit duplicate votes and reports
                  </li>
                  <li>
                    <strong>Journal and lawn content:</strong> Lawn notes, saved care-plan details, measurements, and other content you choose to save
                  </li>
                  <li>
                    <strong>Location:</strong> ZIP code or device location, when you allow it, to provide climate-specific recommendations
                  </li>
                  <li>
                    <strong>Purchase history:</strong> Subscription status and related purchase events used to manage Premium access
                  </li>
                  <li>
                    <strong>Quote requests:</strong> Name, email, phone number, ZIP code, service type, property details, request description, consent record, and optional photos when you ask for lawn or home service quotes
                  </li>
                  <li>
                    <strong>Assistant conversations:</strong> The questions and messages you type into our AI lawn assistant, including when you are not signed in. We keep this text on its own, with no account, device, session, or location identifier attached and no precise timestamp, so we can see what lawn owners are asking and write better answers
                  </li>
                  <li>
                    <strong>Preferences:</strong> Lawn care preferences and analysis results
                  </li>
                  <li>
                    <strong>Product interaction and crash data:</strong> App usage, diagnostics, and crash information used to improve reliability
                  </li>
                </ul>

                <h2 className="text-2xl font-bold text-gray-900 mb-4">Community Gallery</h2>
                <p className="text-gray-700 mb-4">
                  If you submit to the Community gallery, approved content is public and can be viewed and shared by other visitors. This includes the photo, title, grass type, broad region, project type and timing, starting condition, work performed, outcome, and optional social credit you provide. We remove photo metadata such as GPS coordinates during upload and store only the first three digits of the ZIP code you provide. Your optional email address is kept private and used only to tell you about that submission&apos;s moderation status. It does not subscribe you to marketing.
                </p>
                <p className="text-gray-700 mb-8">
                  We use a stable browser identifier to enforce one vote or report per visitor and to reduce abuse. Approved posts may remain available while the gallery operates; pending, rejected, and unreferenced uploads are retained only as needed for moderation, security, and cleanup. You can request removal of a Community post or its associated contact information by emailing support@whatgrassisthis.com.
                </p>

                <h2 className="text-2xl font-bold text-gray-900 mb-4">Quote Requests and Lead Sharing</h2>
                <p className="text-gray-700 mb-6">
                  If you submit a quote request, we collect and store the information needed to review and route that
                  request: your name, email address, phone number, property ZIP code, requested service type, property
                  type, timeline, yard-size range, request description, optional photo, source page, UTM/referrer
                  details, IP address, user agent, and the exact consent text and consent version shown when you
                  submitted the form.
                </p>
                <ul className="list-disc pl-6 mb-8 space-y-2 text-gray-700">
                  <li>WGIS admins review quote requests before any provider handoff.</li>
                  <li>
                    Infrastructure providers help us operate the form, storage, notifications, analytics, and admin
                    workflow.
                  </li>
                  <li>
                    Relevant lawn or home service providers may receive your request details, contact information, and
                    optional photo only when we manually select them as a potential fit for your request.
                  </li>
                  <li>
                    Our v1 quote funnel does not automatically auction leads, broadly resell lead data, or route
                    requests through a ping tree.
                  </li>
                </ul>

                <h2 className="text-2xl font-bold text-gray-900 mb-4">How We Use Your Information</h2>
                <ul className="list-disc pl-6 mb-8 space-y-2 text-gray-700">
                  <li>Analyze your lawn photos using AI to identify grass types</li>
                  <li>Review, publish, rank, and moderate Community gallery submissions and notify submitters about moderation status</li>
                  <li>Create personalized care plans based on your location and preferences</li>
                  <li>Send you seasonal lawn care tips and reminders (if you opt in)</li>
                  <li>Review and fulfill quote requests, including contacting you and sharing request details with relevant lawn or home service providers when appropriate</li>
                  <li>Manage your subscription, purchases, and account access</li>
                  <li>Prevent abuse of anonymous scan limits and protect service availability</li>
                  <li>Improve our prompts, our model choices, and service quality</li>
                </ul>

                <h2 className="text-2xl font-bold text-gray-900 mb-4">AI Processing and Model Training</h2>
                <p className="text-gray-700 mb-4">
                  Grass identification, weed identification, lawn diagnosis, and the lawn assistant are powered by OpenAI. When you upload a photo or ask the assistant a question, we send that content to OpenAI so it can produce your result.
                </p>
                <p className="text-gray-700 mb-4">
                  OpenAI is also permitted to use what we send, including the lawn and weed photos you upload and the text of your questions, to develop and train its models. This is broader than using your content only to answer you, so we want to be direct about it: if you would prefer your photos not be used to train AI models, please do not upload them here.
                </p>
                <p className="text-gray-700 mb-8">
                  To make answers fit your lawn, we may include details such as your ZIP code and your grass type with the question we send. We do not send your name, email address, or account identifiers. If you have questions about this, or want to know what we currently share, email support@whatgrassisthis.com.
                </p>

                <h2 className="text-2xl font-bold text-gray-900 mb-4">Analytics and Performance Tracking</h2>
                <p className="text-gray-700 mb-6">
                  We use analytics tools to understand how visitors use our website, improve our services, and enhance your experience. These tools collect information about your interactions with our site.
                </p>
                <ul className="list-disc pl-6 mb-6 space-y-2 text-gray-700">
                  <li>
                    <strong>Google Analytics 4:</strong> We use Google Analytics to analyze website traffic and user behavior. This service collects information such as pages visited, time spent on pages, device and browser information, approximate geographic location (based on IP address), and how you arrived at our site. Google Analytics uses cookies to collect this data. You can opt out by installing the{" "}
                    <a href="https://tools.google.com/dlpage/gaoptout" target="_blank" rel="noopener noreferrer" className="text-green-600 hover:text-green-700 font-semibold">
                      Google Analytics Opt-out Browser Add-on
                    </a>
                    .
                  </li>
                  <li>
                    <strong>PostHog:</strong> We use PostHog for product analytics, including feature usage tracking, user journey analysis, and session recordings. Session recordings capture your interactions with our site (such as clicks, scrolls, and page navigation) to help us identify usability issues and improve our service. PostHog does not record sensitive information such as passwords or payment details. For more information, visit{" "}
                    <a href="https://posthog.com/privacy" target="_blank" rel="noopener noreferrer" className="text-green-600 hover:text-green-700 font-semibold">
                      PostHog's Privacy Policy
                    </a>
                    .
                  </li>
                </ul>
                <p className="text-gray-700 mb-8">
                  The data collected through these analytics tools helps us understand which features are most useful, identify technical issues, and make data-driven improvements to our lawn care tools and content.
                </p>

                <h2 className="text-2xl font-bold text-gray-900 mb-4">Advertising and Third-Party Services</h2>
                <p className="text-gray-700 mb-6">
                  We work with third-party advertising partners to display advertisements that help fund our free tools and content. These partners use cookies, device and online identifiers, and similar technologies to serve and measure ads based on your visits to our site and to other sites and apps across the internet.
                </p>
                <ul className="list-disc pl-6 mb-6 space-y-2 text-gray-700">
                  <li>
                    <strong>Journey by Mediavine (Grow):</strong> We participate in the Journey advertising program operated by Mediavine through its Grow platform. The Grow script collects site usage and engagement data, and it may load advertising identity technologies, including Unified ID 2.0 (UID2) and European Unified ID (EUID). These use online identifiers derived from information such as your IP address, and your email address if you provide one, to support interest-based advertising and ad measurement across sites. For more information, see{" "}
                    <a href="https://www.mediavine.com/legal-and-privacy-center/" target="_blank" rel="noopener noreferrer" className="text-green-600 hover:text-green-700 font-semibold">
                      the Mediavine Privacy Notice
                    </a>
                    .
                  </li>
                  <li>
                    <strong>Opt-Out:</strong> You can opt out of personalized advertising by visiting{" "}
                    <a href="https://myadcenter.google.com/" target="_blank" rel="noopener noreferrer" className="text-green-600 hover:text-green-700 font-semibold">
                      Google's Ad Settings
                    </a>
                    {" "}or by visiting{" "}
                    <a href="https://www.aboutads.info/choices/" target="_blank" rel="noopener noreferrer" className="text-green-600 hover:text-green-700 font-semibold">
                      www.aboutads.info/choices
                    </a>
                  </li>
                  <li>
                    <strong>Third-Party Cookies:</strong> Third-party vendors, including Google, use cookies to serve ads based on a user's prior visits to our website or other websites.
                  </li>
                </ul>
                <p className="text-gray-700 mb-8">
                  For more information about how Google uses data when you use our site, please visit{" "}
                  <a href="https://policies.google.com/technologies/partner-sites" target="_blank" rel="noopener noreferrer" className="text-green-600 hover:text-green-700 font-semibold">
                    Google's Privacy & Terms
                  </a>
                  .
                </p>

                <h2 className="text-2xl font-bold text-gray-900 mb-4">Data Sharing</h2>
                <p className="text-gray-700 mb-6">
                  We do not sell your personal information for money. However, to help fund our free tools and content,
                  we share certain online identifiers and activity data with advertising partners to deliver
                  interest-based ads. Under some state privacy laws (such as the California CPRA), this may be considered
                  a &ldquo;sale&rdquo; or &ldquo;sharing&rdquo; of personal information, and you can opt out as described
                  in the Advertising and Your Rights sections. Apart from that, we share your personal information only in
                  the situations described in this policy:
                </p>
                <ul className="list-disc pl-6 mb-8 space-y-2 text-gray-700">
                  <li>With your explicit consent</li>
                  <li>To comply with legal requirements</li>
                  <li>
                    With service providers who help us operate our platform (under strict confidentiality agreements)
                  </li>
                  <li>
                    With lawn or home service providers when you submit a quote request and consent to being contacted about that request
                  </li>
                  <li>
                    With analytics providers (Google Analytics and PostHog) as described in the Analytics section above
                  </li>
                  <li>
                    With advertising partners, including Journey by Mediavine (Grow), together with
                    their advertising identity and measurement providers, as described in the Advertising section above
                  </li>
                </ul>

                <h2 className="text-2xl font-bold text-gray-900 mb-4">Your Rights</h2>
                <ul className="list-disc pl-6 mb-8 space-y-2 text-gray-700">
                  <li>Access your personal data</li>
                  <li>Correct inaccurate information</li>
                  <li>Delete your account and data</li>
                  <li>Opt out of marketing communications</li>
                  <li>Opt out of the &ldquo;sale&rdquo; or &ldquo;sharing&rdquo; of your personal information for targeted advertising</li>
                  <li>Export your data</li>
                </ul>

                <h2 className="text-2xl font-bold text-gray-900 mb-4">Contact Us</h2>
                <p className="text-gray-700">
                  If you have questions about this Privacy Policy, please contact us at{" "}
                  <a href="mailto:support@whatgrassisthis.com" className="text-green-600 hover:text-green-700 font-semibold">
                    support@whatgrassisthis.com
                  </a>
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  )
}
